import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { safeJsonParse } from 'shared/utils/securityUtils';

export interface PermissionsState {
  camera: PermissionState | 'unknown';
  microphone: PermissionState | 'unknown';
  geolocation: PermissionState | 'unknown';
}

export interface PersistedPermissions {
  permissions: PermissionsState;
  lastChecked: number;
  version: string;
}

export interface PermissionsContextType {
  permissions: PermissionsState | null;
  setPermissions: (permissions: PermissionsState) => void;
  showPermissionsCheck: boolean;
  setShowPermissionsCheck: (show: boolean) => void;
  savePermissionsToStorage: (permissions: PermissionsState) => void;
  initializePermissions: () => void;
}

const PERMISSIONS_STORAGE_KEY = 'app-permissions';
const PERMISSIONS_VERSION = '1.0';

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<PermissionsState | null>(null);
  const [showPermissionsCheck, setShowPermissionsCheck] = useState(false);

  const loadPersistedPermissions = (): PersistedPermissions | null => {
    const stored = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
    if (stored) {
      return safeJsonParse<PersistedPermissions | null>(stored, null);
    }
    return null;
  };

  const savePermissionsToStorage = useCallback((perms: PermissionsState) => {
    try {
      const data: PersistedPermissions = {
        permissions: perms,
        lastChecked: Date.now(),
        version: PERMISSIONS_VERSION,
      };
      localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // silently fail
    }
  }, []);

  const initializePermissions = useCallback(() => {
    const persisted = loadPersistedPermissions();
    if (persisted) {
      setPermissions(persisted.permissions);
    }
    // Permissions modal disabled globally — requested on-demand when needed (e.g. camera for photos)
   
  }, []);

  const value = useMemo<PermissionsContextType>(() => ({
    permissions,
    setPermissions,
    showPermissionsCheck,
    setShowPermissionsCheck,
    savePermissionsToStorage,
    initializePermissions,
  }), [permissions, showPermissionsCheck, savePermissionsToStorage, initializePermissions]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
